/**
 * User-scoped vehicles API client.
 *
 * Wraps `GET /api/vehicles/mine`, the canonical registry endpoint
 * that returns every vehicle whose owner_email (or legacy phone
 * fallback) matches the authenticated user. Scoping is enforced
 * entirely server-side from the JWT — this client never sends the
 * user id, email, or phone on the wire.
 *
 * See sw-ev-api/controllers/vehiclesPublicController.js
 * (`listMineForCurrentUser`) for the source of truth on the DTO
 * shape and audit reasoning.
 */

import authClient from '../auth/authClient.js';

/**
 * Return the list of registry vehicles that belong to the current
 * signed-in user. Empty array on a genuine no-match. Throws on
 * network / 5xx so callers can render a retry state.
 */
export async function getMyVehicles() {
  const { data } = await authClient.get('/vehicles/mine');
  return Array.isArray(data?.vehicles) ? data.vehicles : [];
}

/**
 * PATCH /vehicles/mine/:vehicleId
 * Update metadata on a vehicle the caller owns. Only allowed fields:
 * vehicleName, make, model, year, color. Licence plate is NOT accepted
 * here — use changeMyVehiclePlate().
 */
export async function updateMyVehicle(vehicleId, patch) {
  const { data } = await authClient.patch(
    `/vehicles/mine/${encodeURIComponent(vehicleId)}`,
    patch,
  );
  return data?.vehicle || null;
}

/**
 * PUT /vehicles/mine/:vehicleId/plate
 * Change the licence plate of a vehicle the caller owns. Applied at once
 * when the plate is free — there is no review step.
 *
 * Resolves `{ changed, vehicle }` (`changed: false` when the plate is
 * already this vehicle's plate in another spelling). Rejects with
 * 409 PLATE_ALREADY_REGISTERED when the plate is registered elsewhere.
 */
export async function changeMyVehiclePlate(vehicleId, licensePlate) {
  const { data } = await authClient.put(
    `/vehicles/mine/${encodeURIComponent(vehicleId)}/plate`,
    { licensePlate },
  );
  return { changed: Boolean(data?.changed), vehicle: data?.vehicle || null };
}
